import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";

const ORDERS_API = process.env.REACT_APP_ORDERS_API;
const DOWNLOAD_API = process.env.REACT_APP_DOWNLOAD_API;
const INVOICE_API = process.env.REACT_APP_INVOICE_API;

// Theme colors
const colors = {
  bgPrimary: 'var(--bg-primary)',
  bgSecondary: 'var(--bg-secondary)',
  textPrimary: 'var(--text-primary)',
  textSecondary: 'var(--text-secondary)',
  accent: 'var(--accent-primary)',
};

const Orders = () => {
  const { user, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState(null); // Track which item is downloading
  const [invoiceLoadingId, setInvoiceLoadingId] = useState(null); // Track which order's invoice is loading

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setIsLoading(false);
      return;
    }

    const fetchOrders = async () => {
      try {
        const params = new URLSearchParams();
        params.append('user_id', user.sub);
        if (user.email) {
          params.append('email', user.email);
        }
        // No credentials: this API authenticates by user_id+email query params and
        // returns Allow-Origin: * — browsers hard-block *+credentials, so sending
        // the cookie would fail the request. Query-param auth needs no cookie.
        const response = await fetch(`${ORDERS_API}?${params.toString()}`);

        if (response.ok) {
          const data = await response.json();
          setOrders(data.orders || []);
        } else {
          setError('Failed to load orders');
        }
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError('Failed to load orders');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, [user, authLoading]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateShort = (dateString) => {
    return new Date(dateString).toISOString().split('T')[0];
  };

  const handleDownload = async (orderId, artworkId) => {
    const downloadKey = `${orderId}-${artworkId}`;
    setDownloadingId(downloadKey);
    try {
      // Build params - include email since orders may be stored with email as user_id
      const params = new URLSearchParams();
      params.append('user_id', user.sub);
      params.append('order_id', orderId);
      params.append('artwork_id', artworkId);
      if (user.email) {
        params.append('email', user.email);
      }
      // query-param auth + Allow-Origin: * — no cookie (see fetchOrders note).
      const response = await fetch(`${DOWNLOAD_API}?${params.toString()}`);

      if (response.ok) {
        const data = await response.json();
        // Use link click instead of window.open to avoid Safari popup blocker
        const link = document.createElement('a');
        link.href = data.download_url;
        link.download = ''; // Triggers download behavior
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert('Failed to generate download link');
      }
    } catch (err) {
      console.error('Download error:', err);
      alert('Failed to download');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleViewInvoice = async (orderId) => {
    setInvoiceLoadingId(orderId);
    try {
      // Build params - include email since orders may be stored with email as user_id
      const params = new URLSearchParams();
      params.append('user_id', user.sub);
      params.append('order_id', orderId);
      if (user.email) {
        params.append('email', user.email);
      }
      // query-param auth + Allow-Origin: * — no cookie (see fetchOrders note).
      const response = await fetch(`${INVOICE_API}?${params.toString()}`);

      if (response.ok) {
        const data = await response.json();
        // Use link click instead of window.open to avoid Safari popup blocker
        const link = document.createElement('a');
        link.href = data.receipt_url;
        link.target = '_blank'; // Open in new tab for receipt viewing
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert('Failed to load receipt');
      }
    } catch (err) {
      console.error('Invoice error:', err);
      alert('Failed to load receipt');
    } finally {
      setInvoiceLoadingId(null);
    }
  };

  if (authLoading) {
    return (
      <div style={{ backgroundColor: colors.bgPrimary, minHeight: '100vh', paddingTop: 'calc(var(--navbar-height) + 4rem)' }}>
        <div className="text-center py-12">
          <p style={{ color: colors.textSecondary }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ backgroundColor: colors.bgPrimary, minHeight: '100vh', paddingTop: 'calc(var(--navbar-height) + 4rem)' }}>
        <div className="text-center py-12">
          <h2 className="text-xl font-medium mb-4" style={{ color: colors.textPrimary }}>Please log in to view your orders</h2>
          <Link to="/login" className="inline-block rounded-md px-6 py-3 text-sm font-medium text-white" style={{ backgroundColor: colors.accent }}>
            Log In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Orders - limkc.com</title>
      </Helmet>

      <div className="w-full h-full overflow-hidden">
        <div
          className="px-4 py-12 min-h-screen"
          style={{
            paddingTop: 'calc(var(--navbar-height) + 4rem)',
            backgroundColor: 'transparent',
            color: colors.textPrimary,
          }}
        >
          <div className="max-w-4xl mx-auto">
          <div className="max-w-xl">
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl" style={{ color: colors.textPrimary }}>Order history</h1>
          </div>

          <div className="mt-16">
            <h2 className="sr-only">Recent orders</h2>

            {isLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-4 border-t-transparent rounded-full mx-auto mb-4" style={{ borderColor: colors.accent, borderTopColor: 'transparent' }}></div>
                <p style={{ color: colors.textSecondary }}>Loading orders...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-red-500 mb-4">{error}</p>
                <button onClick={() => window.location.reload()} style={{ color: colors.accent }}>Try again</button>
              </div>
            ) : orders.length === 0 ? (
              <div className="text-center py-12">
                <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: colors.bgSecondary }}>
                  <svg className="w-8 h-8" style={{ color: colors.textSecondary }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
                <h2 className="text-xl font-medium mb-2" style={{ color: colors.textPrimary }}>No orders yet</h2>
                <p className="mb-6" style={{ color: colors.textSecondary }}>Start shopping to see your orders here.</p>
                <Link to="/arts" className="inline-block rounded-md px-6 py-3 text-sm font-medium text-white shadow-sm hover:opacity-90" style={{ backgroundColor: colors.accent }}>
                  Browse Gallery
                </Link>
              </div>
            ) : (
              <div className="space-y-20">
                {orders.map((order) => (
                  <div key={order.order_id}>
                    <h3 className="sr-only">Order placed on <time dateTime={formatDateShort(order.created_at)}>{formatDate(order.created_at)}</time></h3>

                    <div className="rounded-lg px-4 py-6 sm:flex sm:items-center sm:justify-between sm:space-x-6 sm:px-6 lg:space-x-8" style={{ backgroundColor: colors.bgSecondary }}>
                      <dl className="flex-auto divide-y text-sm sm:grid sm:grid-cols-3 sm:gap-x-6 sm:divide-y-0 lg:w-1/2 lg:flex-none lg:gap-x-8" style={{ color: colors.textSecondary, borderColor: colors.bgPrimary }}>
                        <div className="flex justify-between py-4 sm:block sm:py-0">
                          <dt className="font-medium" style={{ color: colors.textPrimary }}>Date placed</dt>
                          <dd className="sm:mt-1">
                            <time dateTime={formatDateShort(order.created_at)}>{formatDate(order.created_at)}</time>
                          </dd>
                        </div>
                        <div className="flex justify-between py-4 sm:block sm:py-0">
                          <dt className="font-medium" style={{ color: colors.textPrimary }}>Order number</dt>
                          <dd className="sm:mt-1">{order.display_order_id || 'N/A'}</dd>
                        </div>
                        <div className="flex justify-between py-4 sm:block sm:py-0">
                          <dt className="font-medium" style={{ color: colors.textPrimary }}>Total amount</dt>
                          <dd className="font-medium sm:mt-1" style={{ color: colors.textPrimary }}>${(order.amount_total / 100).toFixed(2)}</dd>
                        </div>
                      </dl>
                      <button
                        className="mt-6 flex w-full items-center justify-center rounded-md border px-4 py-2 text-sm font-medium shadow-sm hover:opacity-80 sm:mt-0 sm:w-auto disabled:opacity-70"
                        style={{ borderColor: colors.textSecondary, backgroundColor: colors.bgPrimary, color: colors.textPrimary }}
                        disabled={invoiceLoadingId === order.order_id}
                        onClick={() => handleViewInvoice(order.order_id)}
                      >
                        {invoiceLoadingId === order.order_id ? (
                          <>
                            <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full mr-2"></div>
                            Loading...
                          </>
                        ) : (
                          'View Receipt'
                        )}
                      </button>
                    </div>

                    <table className="mt-4 w-full sm:mt-6" style={{ color: colors.textSecondary }}>
                      <caption className="sr-only">Products</caption>
                      <thead className="sr-only text-left text-sm sm:not-sr-only">
                        <tr>
                          <th scope="col" className="py-3 pr-8 font-normal sm:w-2/5 lg:w-1/3">Product</th>
                          <th scope="col" className="hidden py-3 pr-8 font-normal sm:table-cell">Price</th>
                          <th scope="col" className="hidden py-3 pr-8 font-normal sm:table-cell">Status</th>
                          <th scope="col" className="py-3 text-right font-normal whitespace-nowrap">Info</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y border-b text-sm sm:border-t" style={{ borderColor: colors.bgSecondary }}>
                        {order.items.map((item, idx) => (
                          <tr key={idx} style={{ borderColor: colors.bgSecondary }}>
                            <td className="py-6 pr-8">
                              <div className="flex items-center">
                                <img
                                  src={item.image || `https://limkc.com/arts/${item.artwork_id}/thumb.jpg`}
                                  alt={item.title}
                                  className="mr-6 size-16 rounded-sm object-cover"
                                  style={{ backgroundColor: colors.bgSecondary }}
                                  onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                                />
                                <div>
                                  <div className="font-medium" style={{ color: colors.textPrimary }}>{item.title}</div>
                                  <div className="mt-1 sm:hidden">${item.price?.toFixed(2)}</div>
                                </div>
                              </div>
                            </td>
                            <td className="hidden py-6 pr-8 sm:table-cell">${item.price?.toFixed(2)}</td>
                            <td className="hidden py-6 pr-8 sm:table-cell">
                              {item.size === 'digital' ? 'Ready to download' : item.size === 'donate' ? 'Completed' : 'Processing'}
                            </td>
                            <td className="py-6 text-right font-medium whitespace-nowrap">
                              {item.size === 'digital' ? (
                                <button
                                  onClick={() => handleDownload(order.order_id, item.artwork_id)}
                                  disabled={downloadingId === `${order.order_id}-${item.artwork_id}`}
                                  className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-white shadow-sm hover:opacity-90 disabled:opacity-70"
                                  style={{ backgroundColor: colors.accent }}
                                >
                                  {downloadingId === `${order.order_id}-${item.artwork_id}` ? (
                                    <>
                                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                                      Preparing...
                                    </>
                                  ) : (
                                    <>
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                      </svg>
                                      Download
                                    </>
                                  )}
                                </button>
                              ) : item.artwork_id && item.artwork_id !== 'donate' ? (
                                <Link to={`/arts/${item.artwork_id}`} style={{ color: colors.accent }}>
                                  View<span className="hidden lg:inline"> Product</span>
                                </Link>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
          </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Orders;
